import { Box, Image } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';

import { GaugeAnimation, GaugeLevel } from '../../assets/Images';
import useImageLoader from '../../hooks/useImageLoader';
import DisplayBox from './DisplayBox';

const DEG_PER_LEVEL = 23;
const LEVELS_FROM_ZERO_TO_FIRST_LEVEL = 3;

type GaugeProps = {
  gaugeLevel: number;
  setGaugeLevel: (value: number) => void;
  showAnimation?: boolean;
};

const Gauge = React.forwardRef<HTMLDivElement, GaugeProps>(
  ({ gaugeLevel, setGaugeLevel, showAnimation = true }, ref) => {
    const [showLoader, setShowLoader] = useState(true);
    const [isHovering, setIsHovering] = useState(false);
    const [hoverLevel, setHoverLevel] = useState(10);

    useEffect(() => {
      const timeout = setTimeout(() => {
        setShowLoader(false);
      }, 2500);
      return () => clearTimeout(timeout);
    }, []);

    const calculateGaugeLevel = useCallback(
      (
        event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
        saveLevel = false,
      ) => {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const x = 'clientX' in event ? event.clientX : event.touches[0].clientX;
        const y = 'clientY' in event ? event.clientY : event.touches[0].clientY;
        const levelPadding = 'clientX' in event ? 2 : 5; // gives extra padding for touch events

        const angle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);
        // normalize the angle so 0 is straight down and increase clockwise
        const normalizedAngle = (angle - 90 + 360) % 360;

        // 23 is the angle between each level, 3 is how many levels from straight down to the start.
        const level =
          Math.ceil((normalizedAngle - levelPadding) / DEG_PER_LEVEL) -
          LEVELS_FROM_ZERO_TO_FIRST_LEVEL;
        const clampedLevel = Math.max(0, Math.min(10, level));

        if (
          normalizedAngle < DEG_PER_LEVEL * LEVELS_FROM_ZERO_TO_FIRST_LEVEL - 8 || // allow -8 deg for 0 score
          normalizedAngle > DEG_PER_LEVEL * (10 + LEVELS_FROM_ZERO_TO_FIRST_LEVEL)
        ) {
          setIsHovering(false);
          return;
        }
        setIsHovering(true);

        if (hoverLevel !== clampedLevel) {
          setHoverLevel(clampedLevel);
        }

        if (saveLevel) {
          setGaugeLevel(clampedLevel);
          setIsHovering(false);
        }
      },
      [hoverLevel, setGaugeLevel],
    );

    const shownLevel = isHovering ? hoverLevel : gaugeLevel;

    const imagesLoading = useImageLoader(Object.values(GaugeLevel));

    if (showAnimation && (showLoader || imagesLoading)) {
      return (
        <Box ref={ref} width="300px" height="300px" position="relative" pointerEvents="none">
          <Image src={GaugeAnimation} alt="Gauge" width="100%" height="100%" borderRadius="50%" />
          <DisplayBox />
        </Box>
      );
    }

    return (
      <Box ref={ref}>
        <Box
          width="300px"
          height="300px"
          position="relative"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onMouseMove={calculateGaugeLevel}
          onClick={(event) => calculateGaugeLevel(event, true)}
          onTouchMove={(event) => calculateGaugeLevel(event, true)}
          cursor="pointer"
          style={{
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <Image
            src={GaugeLevel[shownLevel as keyof typeof GaugeLevel]}
            alt="Gauge"
            position="absolute"
            zIndex={1}
            pointerEvents="none"
          />
          <DisplayBox text={shownLevel} />
        </Box>
      </Box>
    );
  },
);

Gauge.displayName = 'Gauge';

export default Gauge;
